'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { readAllPages } from '@/lib/supabasePaging'
import { isMissingProjectKindColumnError } from '@/lib/projects'
import { listTeamMembers, listTeamInvitations, getPendingInvitations } from '@/lib/api'
import type { TeamMember, TeamInvitation, PendingInvitation } from '@/lib/api'

// =============================================================================
// useTeamBySlug — resolve team slug to full team object
// =============================================================================

interface TeamInfo {
  id: string
  name: string
  slug: string
  defaultProjectBillingMode: 'team_tab' | 'generator_pays'
}

interface UseTeamBySlugResult {
  team: TeamInfo | null
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

export function useTeamBySlug(slug: string | null): UseTeamBySlugResult {
  const [team, setTeam] = useState<TeamInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  // Latest-value mirror for the stale-response guard below. Assigned in the
  // fetch effect (not render — react-hooks/refs); refresh() reads it too.
  const slugRef = useRef(slug)

  const fetchTeam = useCallback(async () => {
    const currentSlug = slugRef.current
    if (!currentSlug) {
      setIsLoading(false)
      return
    }

    try {
      // slug column exists via migration but may not be in generated types yet
      const { data, error: queryError } = await supabase
        .from('organizations')
        .select('id, name, slug, default_project_billing_mode')
        .eq('slug', currentSlug as never)
        .eq('type', 'team')
        .is('deleted_at', null)
        .single()

      // Guard against stale responses: if slug changed while request was in flight, discard
      if (slugRef.current !== currentSlug) return

      const row = data as unknown as {
        id: string
        name: string
        slug: string
        default_project_billing_mode: 'team_tab' | 'generator_pays' | null
      } | null
      if (queryError || !row) {
        setError(new Error('Team not found'))
        setTeam(null)
      } else {
        setTeam({
          id: row.id,
          name: row.name,
          slug: row.slug,
          defaultProjectBillingMode:
            row.default_project_billing_mode === 'generator_pays' ? 'generator_pays' : 'team_tab',
        })
        setError(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch team'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    slugRef.current = slug
    if (!slug) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- no fetch will run without a slug; clear the initial loading flag
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    fetchTeam()
  }, [slug, fetchTeam])

  return { team, isLoading, error, refresh: fetchTeam }
}

// =============================================================================
// useTeamMembers — fetch team members via API
// =============================================================================

interface UseTeamMembersResult {
  members: TeamMember[]
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

export function useTeamMembers(teamId: string | null): UseTeamMembersResult {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchMembers = useCallback(async () => {
    if (!teamId) {
      setIsLoading(false)
      return
    }

    try {
      const result = await listTeamMembers(teamId)
      setMembers(result.members)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch members'))
    } finally {
      setIsLoading(false)
    }
  }, [teamId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- flag the in-flight fetch; result states land post-await inside fetchMembers
    setIsLoading(true)
    fetchMembers()
  }, [fetchMembers])

  return { members, isLoading, error, refresh: fetchMembers }
}

// =============================================================================
// useTeamProjects — fetch projects for a team via Supabase
// =============================================================================

interface TeamProject {
  id: string
  name: string
  createdBy: string
  billingMode: string
  createdAt: string | null
}

interface UseTeamProjectsResult {
  projects: TeamProject[]
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

export function useTeamProjects(teamId: string | null): UseTeamProjectsResult {
  const [projects, setProjects] = useState<TeamProject[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchProjects = useCallback(async () => {
    if (!teamId) {
      setIsLoading(false)
      return
    }

    try {
      const queryProjects = (hideSystemProjects: boolean) => {
        let query = supabase
          .from('projects')
          .select('id, name, created_by, billing_mode, created_at')
          .eq('team_id', teamId)
          .is('deleted_at', null)
        // System projects ("API Generations", "Workflow runs") are API plumbing, reached by link only.
        if (hideSystemProjects) query = query.filter('kind', 'eq', 'user')
        return query
      }
      // Every page: past a thousand projects the rest would silently drop out.
      const paging = { key: 'id', newestFirst: 'created_at' }
      let result = await readAllPages(() => queryProjects(true), paging)
      if (result.error && isMissingProjectKindColumnError(result.error)) {
        result = await readAllPages(() => queryProjects(false), paging)
      }
      const { data, error: queryError } = result

      if (queryError) throw queryError

      setProjects(
        (data ?? []).map(p => ({
          id: p.id,
          name: p.name,
          createdBy: p.created_by,
          billingMode: p.billing_mode,
          createdAt: p.created_at,
        })),
      )
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch projects'))
    } finally {
      setIsLoading(false)
    }
  }, [teamId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- flag the in-flight fetch; result states land post-await inside fetchProjects
    setIsLoading(true)
    fetchProjects()
  }, [fetchProjects])

  return { projects, isLoading, error, refresh: fetchProjects }
}

// =============================================================================
// useTeamInvitations — fetch pending invitations for a team (admin)
// =============================================================================

interface UseTeamInvitationsResult {
  invitations: TeamInvitation[]
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

export function useTeamInvitations(teamId: string | null): UseTeamInvitationsResult {
  const [invitations, setInvitations] = useState<TeamInvitation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchInvitations = useCallback(async () => {
    if (!teamId) {
      setIsLoading(false)
      return
    }

    try {
      const result = await listTeamInvitations(teamId)
      setInvitations(result.invitations)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch invitations'))
    } finally {
      setIsLoading(false)
    }
  }, [teamId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- flag the in-flight fetch; result states land post-await inside fetchInvitations
    setIsLoading(true)
    fetchInvitations()
  }, [fetchInvitations])

  return { invitations, isLoading, error, refresh: fetchInvitations }
}

// =============================================================================
// usePendingInvitations — invitations for the current user
// =============================================================================

interface UsePendingInvitationsResult {
  invitations: PendingInvitation[]
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

export function usePendingInvitations(options: { enabled?: boolean } = {}): UsePendingInvitationsResult {
  const enabled = options.enabled ?? true
  const [invitations, setInvitations] = useState<PendingInvitation[]>([])
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)
  // State (not a ref): the value is read during render below (react-hooks/refs).
  const [hasFetched, setHasFetched] = useState(false)
  const requestIdRef = useRef(0)

  const fetchInvitations = useCallback(async () => {
    if (!enabled) {
      requestIdRef.current += 1
      setHasFetched(false)
      setIsLoading(false)
      return
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setIsLoading(true)
    try {
      const result = await getPendingInvitations()
      if (requestIdRef.current !== requestId) return
      setInvitations(result.invitations)
      setError(null)
    } catch (err) {
      if (requestIdRef.current !== requestId) return
      setError(err instanceof Error ? err : new Error('Failed to fetch invitations'))
    } finally {
      if (requestIdRef.current !== requestId) return
      setHasFetched(true)
      setIsLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- flag the in-flight fetch; result states land post-await inside fetchInvitations
    fetchInvitations()
  }, [fetchInvitations])

  return {
    invitations,
    isLoading: enabled && !hasFetched ? true : isLoading,
    error,
    refresh: fetchInvitations,
  }
}
